import React, { useEffect } from 'react';
import Cookies from 'universal-cookie';

import { getConfig } from '@edx/frontend-platform';
import NotificationsTray from '@edx/frontend-plugin-notifications';

function addPlugins(config, slot_name, plugins) {
  if (slot_name in config.pluginSlots === false) {
    config.pluginSlots[slot_name] = {
      keepDefault: true,
      plugins: []
    };
  }

  config.pluginSlots[slot_name].plugins.push(...plugins);
}

let themeCookie = 'indigo-toggle-dark';
let themeCookieExpiry = 90; // days

const AddDarkTheme = () => {
  const cookies = new Cookies();
  const isThemeToggleEnabled = getConfig().INDIGO_ENABLE_DARK_TOGGLE;

  const getCookieExpiry = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate() + themeCookieExpiry);
  };

  const getCookieOptions = () => {
    const serverURL = new URL(getConfig().LMS_BASE_URL);
    const options = { domain: serverURL.hostname, path: '/', expires: getCookieExpiry() };
    return options;
  };

  const addDarkThemeToIframes = () => {
    const iframes = document.getElementsByTagName('iframe');
    const iframesLength = iframes.length;
    if (iframesLength > 0) {
      Array.from({ length: iframesLength }).forEach((_, index) => {
        const style = document.createElement('style');
        style.textContent = `
          body{
            background-color: #0D0D0E;
            color: #ccc;
          }
          a {color: #ccc;}
          a:hover{color: #d3d3d3;}
          `;
        if (iframes[index].contentDocument) { iframes[index].contentDocument.head.appendChild(style); }
      });
    }
  };

  useEffect(() => {
    const theme = cookies.get(themeCookie);

    // - When page loads, Footer loads before MFE content. Since there is no iframe on page,
    // it does not append any class. MutationObserver observes changes in DOM and hence appends dark
    // attributes when iframe is added. After 15 sec, this observer is destroyed to conserve resources. 
    // - It has been added outside dark-theme condition so that it can be removed on Component Unmount.
    // - Observer can be passed to `addDarkThemeToIframes` function and disconnected after observing Iframe.
    // This approach has a limitation: the observer first detects the iframe and then detects the docSrc. 
    // We need to wait for docSrc to fully load before appending the style tag.
    const observer = new MutationObserver(() => {
      addDarkThemeToIframes();
    });

    if (isThemeToggleEnabled && theme === 'dark') {
      document.body.classList.add('indigo-dark-theme');
      
      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => observer?.disconnect(), 15000); // clear after 15 sec to avoid resource usage

      cookies.set(themeCookie, theme, getCookieOptions());      //  on page load, update expiry
    }

    return () => observer?.disconnect();
  }, []);

  return (<div />);
};

async function setConfig () {
  let config = {
    pluginSlots: {}
  };

  try {
    /* We can't assume FPF exists, as it's not declared as a dependency in all
     * MFEs, so we import it dynamically. In addition, for dynamic imports to
     * work with Webpack all of the code that actually uses the imported module
     * needs to be inside the `try{}` block.
     */
    const { DIRECT_PLUGIN, PLUGIN_OPERATIONS } = await import('@openedx/frontend-plugin-framework');
const { useState, useEffect } = await import ('react');
const { FontAwesomeIcon: FAIcon }  = await import('@fortawesome/react-fontawesome');
const { getConfig } = await import ('@edx/frontend-platform');
const { faTwitter, faLinkedin, faFacebook, faInstagram, faMedium, faYoutube } = await import('@fortawesome/free-brands-svg-icons');


// const { default: IndigoFooter } = await import('@edly-io/indigo-frontend-component-footer');

const footerStyle = { backgroundColor: '#3B424A', color: '#fff', padding: '25px 40px' };
const containerStyle = { maxWidth: '1200px', width: '100%', margin: '0 auto' };
const linkListStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '40px',
  padding: '8px 0 30px 0',
  listStyleType: 'none',
  borderBottom: '2px solid rgba(255, 255, 255, 0.75)',
  marginBottom: '24px',
  flexWrap: 'wrap',
};
const linkStyle = { color: '#fff', fontWeight: '600', fontSize: '16px' };
const footerBottomStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
};
const footerLink = {color: '#fff', fontSize: '28px' };
const socialListStyle = {
  display: 'flex',
  marginTop: '0.4rem',
  marginBottom: '2rem',
  listStyle: 'none',
  paddingLeft: '0',
  textAlign: 'center',
  gap: '16px',
};
const copyrightStyle = { margin: '-2px 0 8px 0', fontSize: '0.6875em' };
const CustomFooter = () => {
  const [Icons, setIcons] = useState(null);

  useEffect(() => {
    (async () => {
      const { FontAwesomeIcon } = await import('@fortawesome/react-fontawesome');
      const { faTwitter, faLinkedin, faFacebook, faInstagram, faMedium, faYoutube } = await import('@fortawesome/free-brands-svg-icons');
      setIcons({ FontAwesomeIcon, faTwitter, faLinkedin, faFacebook, faInstagram, faMedium, faYoutube });
    })();
  }, []);

  if (!Icons) return null;
  return (
    <footer style={footerStyle}>
      <div style={containerStyle}>
        <ul style={linkListStyle}>
          <li>
            <a style={linkStyle} href="https://africancitieslab.org/fr/about/" target="_blank" rel="noopener noreferrer">
              À propos
            </a>
          </li>
          <li>
            <a style={linkStyle} href="https://africancitieslab.org/fr/contact/" target="_blank" rel="noopener noreferrer">
              Contact
            </a>
          </li>
          <li>
            <a style={linkStyle} href="https://africancitieslab.org/fr/terms-conditions/" target="_blank" rel="noopener noreferrer">
              Termes & Conditions
            </a>
          </li>
          <li>
            <a style={linkStyle} href="https://africancitieslab.org/fr/gdpr/" target="_blank" rel="noopener noreferrer">
              RGPD
            </a>
          </li>
        </ul>
        <div style={footerBottomStyle}>
          <div>
            <ul style={socialListStyle}>
              <li>
                <a style={footerLink} href="https://twitter.com/AfricanCtiesLab">
                <Icons.FontAwesomeIcon icon={Icons.faTwitter} />
                </a>
              </li>
              <li>
                <a style={footerLink} href="https://www.linkedin.com/company/african-cities-lab/?viewAsMember=true">
                 <Icons.FontAwesomeIcon icon={Icons.faLinkedin} />
                </a>
              </li>
              <li>
                <a style={footerLink} href="https://www.facebook.com/African-Cities-Lab-103073095772023">
                  <Icons.FontAwesomeIcon icon={Icons.faFacebook} />
                </a>
              </li>
              <li>
                <a style={footerLink} href="https://instagram.com/africancitieslab?igshid=YmMyMTA2M2Y=">
                  <Icons.FontAwesomeIcon icon={Icons.faInstagram} />
                </a>
              </li>
              <li>
                <a style={footerLink} href="https://medium.com/@African_Cities_Lab">
                  <Icons.FontAwesomeIcon icon={Icons.faMedium} />
                </a>
              </li>
              <li>
                <a style={footerLink} href="https://www.youtube.com/channel/UC22_61it3OdPBNj3CwkndUA">
                  <Icons.FontAwesomeIcon icon={Icons.faYoutube} />
                </a>
              </li>
            </ul>
            <p style={copyrightStyle}>
              © Copyright 2022 African Cities Lab. All rights reserved.
            </p>
          </div>
          <div>
            <a href="/">
              <img
                src={`${getConfig().LMS_BASE_URL}/theming/asset/images/logo-white.png`}
                alt="African Cities Lab"
                width="175"
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
// Only define them if not already declared
if (typeof useState === "undefined" || typeof useEffect === "undefined") {
  const react = await import("react");
  globalThis.useState = react.useState;
  globalThis.useEffect = react.useEffect;
}

const { getAuthenticatedHttpClient } = await import(
  "@edx/frontend-platform/auth"
);

const modalStyles = {
  overlayStyles: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  containerStyles: {
    width: "90vw",
    height: "90vh",
    backgroundColor: "#fff",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    padding: "20px",
    overflow: "hidden",
  },
  contentStyles: {
    flex: 1,
    overflowY: "auto",
  },
  closeButton: {
    position: "absolute",
    top: "16px",
    right: "16px",
    backgroundColor: "#BE3712",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    padding: "8px 12px",
    cursor: "pointer",
    fontSize: "14px",
  },
  warning: {
    position: "absolute",
    top: "60px",
    right: "16px",
    color: "#ff9781 ",
    fontSize: "12px",
    width: "15%",
    textAlign: "right",
  },
};

const spinnerStyles = {
  bodyStyles: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
  },
  spinner: {
    width: "20px",
    height: "20px",
    border: "2px solid lightgray",
    borderTop: "2px solid #3498db",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
};

const getCookieValue = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
};

const isPreferedEnglish = () => {
  const languagePreference = getCookieValue("openedx-language-preference");
  return languagePreference === "en";
};

const CenteredPopup = () => {
  const [status, setStatus] = useState(false);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [email, setEmail] = useState("");
  const [showSpinner, setShowSpinner] = useState(false);

  const STATUS = {
    show: "show",
    must_show: "must_show",
    dont_show: "dont_show",
  };

  const apiUrl = `${getConfig().LMS_BASE_URL}/api/`;

  const handleOnSkip = () => {
    setShowSpinner(true);

    const fetchStatus = async () => {
      try {
        const response = await getAuthenticatedHttpClient().get(
          apiUrl + "status/"
        );
        const APIStatus = response.data.status;
        if (APIStatus === STATUS.show || APIStatus === STATUS.dont_show) {
          setStatus(STATUS.dont_show);
        } else {
          setStatus(STATUS.must_show);
        }
      } catch {
        setStatus(STATUS.dont_show);
      } finally {
        setShowSpinner(false);
      }
    };

    const timer = setTimeout(fetchStatus, 2000);
    return () => clearTimeout(timer);
  };

  const handleOnLoad = () => {
    const updateFormLoaded = async () => {
      try {
        await getAuthenticatedHttpClient().post(apiUrl + "status/");
      } catch (error) {
        console.log(error);
      }
    };

    updateFormLoaded();
  };

  const warningText = () => {
    let text = "";
    if (count >= 2) {
      text = isPreferedEnglish()
        ? "You must submit this form now then press the Close button to close it."
        : "Vous devez soumettre ce formulaire maintenant, puis appuyer sur le bouton Fermer pour le fermer.";
    } else {
      text = isPreferedEnglish()
        ? `You can skip filling this form ${2 - count} more time(s)`
        : `Vous pouvez ignorer le remplissage de ce formulaire ${
            2 - count
          } fois de plus`;
    }
    return text;
  };

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await getAuthenticatedHttpClient().get(
          apiUrl + "status/"
        );
        setStatus(response.data.status);
        setCount(response.data.count);
        setEmail(response.data.email);
      } catch {
        setStatus(STATUS.dont_show);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, []);

  if (loading || status === STATUS.dont_show) return <></>;

  return (
    <div style={modalStyles.overlayStyles}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={modalStyles.containerStyles}>
        <div style={modalStyles.closeButton} onClick={handleOnSkip}>
          {!showSpinner ? (
            <>{isPreferedEnglish() ? "Close" : "Fermer"}</>
          ) : (
            <div style={spinnerStyles.spinner}></div>
          )}
        </div>
        <div style={modalStyles.warning}>{warningText()}</div>
        <iframe
          src={
            isPreferedEnglish()
              ? `https://docs.google.com/forms/d/e/1FAIpQLScTq5UnWmHFggADMvS1xJVBr42SwhDazYC80mzuKwKm4dkX_w/viewform?embedded=true&usp=pp_url&entry.405262843=${encodeURIComponent(
                  email
                )}`
              : `https://docs.google.com/forms/d/e/1FAIpQLSeF0qxHwIWStxp17gLpzmW4ygI5adL7VzC93lc9nZchK9322w/viewform?embedded=true&usp=pp_url&entry.405262843=${encodeURIComponent(
                  email
                )}`
          }
          width="100%"
          height="1000px"
          frameBorder="0"
          marginHeight="0"
          marginWidth="0"
          title="Google Form"
          onLoad={handleOnLoad}
        >
          Loading…
        </iframe>
      </div>
    </div>
  );
};

const DataMFELink = () => {
  const [isAllowed, setIsAllowed] = useState(false);
  const apiUrl = `${getConfig().LMS_BASE_URL}/api/`;
  const SURVEY_MFE_URL = `https://${getConfig().BASE_URL}/survey/`;
  useEffect(() => {
    const fetchData = async () => {
      try {
        const responses = await getAuthenticatedHttpClient().get(
          apiUrl + "allowed/"
        );
        setIsAllowed(responses.data.is_allowed);
      } catch (error) {
        console.log(error);
      }
    };
    fetchData();
  }, []);
  return (
    <>
      {isAllowed ? (
        <a className="nav-link" href={SURVEY_MFE_URL}>
          DATA MFE
        </a>
      ) : (
        <></>
      )}
    </>
  );
};
    addPlugins(config, 'footer_slot', [
        {
          op: PLUGIN_OPERATIONS.Hide,
          widgetId: 'default_contents',
        }]);
    addPlugins(config, 'footer_slot', [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'custom_footer',
            type: DIRECT_PLUGIN,
            RenderWidget: () => <CustomFooter />
          },
        }]);
    addPlugins(config, 'desktop_secondary_menu_slot', [
        {
          op: PLUGIN_OPERATIONS.Hide,
          widgetId: 'default_contents',
        }]);
    addPlugins(config, 'desktop_secondary_menu_slot', [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'notification_tray_all',
            type: DIRECT_PLUGIN,
            RenderWidget: () => {return <NotificationsTray/>},
          },
        }]);
    if (process.env.APP_ID == 'authn') {
    }
    if (process.env.APP_ID == 'authoring') {
    }
    if (process.env.APP_ID == 'account') {
    }
    if (process.env.APP_ID == 'communications') {
    }
    if (process.env.APP_ID == 'discussions') {
    }
    if (process.env.APP_ID == 'gradebook') {
    }
    if (process.env.APP_ID == 'learner-dashboard') {
      addPlugins(config, 'desktop_main_menu_slot', [
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
                id: 'custom_header',
                type: DIRECT_PLUGIN,
                RenderWidget: () => <DataMFELink />,
            }
        }
    ]);
      addPlugins(config, 'desktop_main_menu_slot', [
        {
            op: PLUGIN_OPERATIONS.Insert,
            widget: {
                id: 'second_header',
                type: DIRECT_PLUGIN,
                RenderWidget: () => <CenteredPopup />,
            }
        }
    ]);
    }
    if (process.env.APP_ID == 'learning') {
      addPlugins(config, 'learning_help_slot', [
        {
          op: PLUGIN_OPERATIONS.Hide,
          widgetId: 'default_contents',
        }]);
      addPlugins(config, 'learning_help_slot', [
        {
          op: PLUGIN_OPERATIONS.Insert,
          widget: {
            id: 'notification_tray_learning',
            type: DIRECT_PLUGIN,
            RenderWidget: () => {return <NotificationsTray/>},
          },
        }]);
    }
    if (process.env.APP_ID == 'ora-grading') {
    }
    if (process.env.APP_ID == 'profile') {
    }
    if (process.env.APP_ID == 'survey') {
    }
  } catch { }

  return config;
}

export default setConfig;